import { useMemo, useState } from "react";
import { Plus, Pencil, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { BRANCH_TYPES } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, Badge, EmptyState } from "@/components/common/States";
import { BranchFormDialog } from "./BranchFormDialog";

export default function BranchesPage() {
  const { can, companyId, refreshBranches } = useSession();
  const enabled = can("branch.view") && !!companyId;
  const { data, loading, error, reload } = useFetch("/branches", null, enabled);
  const [dialog, setDialog] = useState({ open: false, branch: null });
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");

  const rows = useMemo(() => (data || []).filter((b) =>
    (type === "all" || b.type === type) &&
    [b.name, b.code, b.city].join(" ").toLowerCase().includes(q.toLowerCase())), [data, q, type]);

  if (!can("branch.view")) return <NoAccess perm="branch.view" />;
  const onSaved = () => { reload(); refreshBranches(); };
  const typeLabel = (t) => BRANCH_TYPES.find((x) => x.value === t)?.label || t;

  return (
    <div data-testid="branches-page">
      <PageHeader eyebrow="Administration" title="Branches & Workshops" subtitle="Locations that operate under the selected company."
        actions={can("branch.manage") && companyId && <Button onClick={() => setDialog({ open: true, branch: null })} data-testid="add-branch-button"><Plus className="h-4 w-4 mr-1.5" />Add branch</Button>} />
      {!companyId ? (
        <div className="g39-card"><EmptyState title="Select a company" body="Choose a company from the selector in the top bar." /></div>
      ) : (
        <div className="g39-card overflow-hidden">
          <div className="flex flex-col sm:flex-row gap-3 p-4 border-b bg-secondary/40">
            <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input className="pl-9 bg-white" placeholder="Search name, code, city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="branch-search-input" /></div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="sm:w-48 bg-white" data-testid="branch-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All types</SelectItem>{BRANCH_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {loading && <LoadingRows />}
          {error && <ErrorState message={error} onRetry={reload} />}
          {data && !rows.length && <EmptyState title="No branches found" />}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <Table data-testid="branches-table">
                <TableHeader><TableRow><TableHead>Branch</TableHead><TableHead>Type</TableHead><TableHead>Location</TableHead><TableHead>GSTIN</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {rows.map((b) => (
                    <TableRow key={b.id} data-testid={`branch-row-${b.code}`}>
                      <TableCell><div className="font-medium">{b.name}</div><div className="text-xs font-mono-g text-muted-foreground">{b.code}</div></TableCell>
                      <TableCell className="text-sm">{typeLabel(b.type)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{[b.city, b.state].filter(Boolean).join(", ") || "—"}</TableCell>
                      <TableCell className="font-mono-g text-xs">{b.gstin || "—"}</TableCell>
                      <TableCell><Badge tone={b.active ? "active" : "inactive"}>{b.active ? "Active" : "Inactive"}</Badge></TableCell>
                      <TableCell className="text-right">
                        {can("branch.manage") && <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, branch: b })} data-testid={`edit-branch-${b.code}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
      <BranchFormDialog open={dialog.open} branch={dialog.branch} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSaved={onSaved} />
    </div>
  );
}
